import {useMutation, type UseMutationOptions} from '@github-ui/react-query'
import {throwErrorsIfBadResponse} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {useLayoutRouteData} from '../page-data/loaders/use-layout-route-data'
import {usePullRequestBasePath} from '../stores/layout-selectors'
import {reactFetch} from '@github-ui/verified-fetch'
import {removeCommentQueryData} from '../page-data/loaders/use-conversation-comments'

export interface DeleteIssueCommentVariables {
  id: number
}

/**
 * Mutation to delete an issue comment, automatically removing the corresponding item from the `conversation_comments`
 * query store.
 */
export function useDeleteIssueCommentMutation(
  options: Omit<UseMutationOptions<unknown, Error, DeleteIssueCommentVariables, unknown>, 'mutationFn'> = {},
) {
  const basePath = usePullRequestBasePath()
  const {repository} = useLayoutRouteData()

  return useMutation({
    ...options,
    mutationFn: async ({id}: DeleteIssueCommentVariables) => {
      // Comment deletion works repo-wide across PRs and issues with unique comment IDs; it's not scoped to just this PR
      const path = `/${encodeURIComponent(repository.ownerLogin)}/${encodeURIComponent(repository.name)}/issue_comments/${id}`

      const response = await reactFetch(path, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
        },
      })

      throwErrorsIfBadResponse(response)
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      removeCommentQueryData(context.client, basePath, variables.id)
      return options.onSuccess?.(response, variables, onMutateResult, context)
    },
  })
}
